/**
 * Rotate API Key flow for an existing SubContractorHub integration.
 *
 * Story SCH-1.3 (FR4, NFR8, NFR9, NFR12). User pastes a new API key,
 * backend re-validates against SCH, and on success the existing Vault
 * secret is updated in place via update_subcontractorhub_secret. If the
 * new key resolves to a different external_org_name, we show a confirm
 * AlertDialog (paste-error guard, AC5) before completing the rotation.
 *
 * SECURITY (NFR9):
 * - Input is type="password" so the key is masked while typing.
 * - Plaintext is held in component-local React state only — never lifted
 *   to context, never persisted to TanStack Query cache, never logged.
 * - State is cleared synchronously on success and on auth_failed. On
 *   transient connection_test_failed we keep the input so the user can
 *   retry; on org-mismatch we keep it so the user can confirm.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SlidePanel } from '@/components/ui/slide-panel';
import { useToast } from '@/components/ui/use-toast';

import { useRotateSubContractorHubKey } from '@/hooks/integrations/useSubContractorHubConnection';
import type { SubContractorHubIntegration } from '@/types/integrations/subcontractorhub';

interface RotateKeyPanelProps {
  isOpen: boolean;
  onClose: () => void;
  integration: SubContractorHubIntegration;
}

const ERROR_MESSAGES: Record<string, string> = {
  auth_failed: 'Invalid API key. Existing key is unchanged.',
  connection_test_failed: 'Could not reach SubContractorHub. Try again in a moment.',
  integration_not_found: 'Integration not found. Refresh the page and try again.',
  internal_error: 'Could not rotate key. Try again or contact support.',
};

export function RotateKeyPanel({ isOpen, onClose, integration }: RotateKeyPanelProps) {
  const [newKey, setNewKey] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // When non-null, the new key validated but resolved to a different org.
  // Holds the new org name so the AlertDialog can render it and the
  // confirm action can re-issue the mutation. The newKey state is kept
  // intact alongside this so the second call has the value.
  const [pendingOrgName, setPendingOrgName] = useState<string | null>(null);

  const { toast } = useToast();
  const rotateMutation = useRotateSubContractorHubKey();

  const isBusy = rotateMutation.isPending;
  const canSubmit = newKey.trim().length > 0 && !isBusy;

  const handleNewKeyChange = (value: string) => {
    setNewKey(value);
    if (errorMessage !== null) setErrorMessage(null);
  };

  const closeAndReset = () => {
    setNewKey('');
    setErrorMessage(null);
    setPendingOrgName(null);
    onClose();
  };

  const handleSubmit = async (confirmOrgChange: boolean) => {
    setErrorMessage(null);
    try {
      const response = await rotateMutation.mutateAsync({
        apiKey: newKey,
        confirmOrgChange,
      });

      if (response.error) {
        setErrorMessage(
          ERROR_MESSAGES[response.error.code] ?? ERROR_MESSAGES.internal_error,
        );
        if (response.error.code === 'auth_failed') {
          // NFR9: clear plaintext on hard rejection.
          setNewKey('');
        }
        // On connection_test_failed we keep the input so the user can retry.
        return;
      }

      if (response.data?.orgChanged) {
        // AC5: hold for confirm dialog. Keep newKey so the second call has it.
        setPendingOrgName(response.data.externalOrgName);
        return;
      }

      // Real success — toast, clear state synchronously, close panel.
      toast({ title: 'API key rotated successfully' });
      setNewKey('');
      setPendingOrgName(null);
      onClose();
    } catch {
      setErrorMessage(ERROR_MESSAGES.internal_error);
    }
  };

  const handleConfirmOrgChange = () => {
    setPendingOrgName(null);
    void handleSubmit(true);
  };

  const handleCancelOrgChange = () => {
    setPendingOrgName(null);
    // Keep the input so the user can paste a different (correct) key.
  };

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={closeAndReset}
        title="Rotate API Key"
        width="lg"
        topOffset="4.5rem"
      >
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Replace your SubContractorHub API key without losing your existing
            configuration. Your mappings, imported projects, and integration
            settings are preserved.
          </p>

          <div className="space-y-2">
            <Label>Current API key</Label>
            <div className="font-mono text-sm rounded-md border bg-muted/50 px-3 py-2">
              ••••••••{integration.apiKeyLastFour ?? '????'}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rotate-new-key">New API key</Label>
            <Input
              id="rotate-new-key"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="Paste new SubContractorHub API key"
              value={newKey}
              onChange={(e) => handleNewKeyChange(e.target.value)}
              disabled={isBusy}
            />
          </div>

          {errorMessage && (
            <Alert variant="destructive">
              <AlertTitle>Could not rotate key</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closeAndReset} disabled={isBusy}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={!canSubmit}>
              {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Validate and replace
            </Button>
          </div>
        </div>
      </SlidePanel>

      <AlertDialog
        open={pendingOrgName !== null}
        onOpenChange={(open) => {
          if (!open) handleCancelOrgChange();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Different organization detected</AlertDialogTitle>
            <AlertDialogDescription>
              This API key belongs to <strong>{pendingOrgName ?? ''}</strong>,
              not <strong>{integration.externalOrgName ?? '—'}</strong>.
              Continue with the rotation?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelOrgChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmOrgChange}>
              Confirm anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
